import React, { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { isEmpty } from 'lodash';
import classes from '../offers/OffersSelectedFilters.scss'

import CancelIcon from '@mui/icons-material/Cancel';
import EntityChips from 'cityads-v3/components/EntitySelectorWithChips/EntityChips'
import IconButton from '@mui/material/IconButton'
import Paper from '@mui/material/Paper'
import { useFilterLists } from './CouponsSelectedFilters.hooks';
import { useCouponFiltersContext } from './CouponFilters.hooks';
import { HandleDeleteGroupFunction, HandleDeleteItemFunction } from '../offers/OffersSelectedFilters';

export default function CouponsSelectedFilters(): JSX.Element {
  const { t } = useTranslation(['Offers']);
  const {
    regionList,
    categoryList,
    offerList,
    promoActionTypeList,
    promoCouponStatusList,
    languageList,
    couponSectorList,
    couponScopeList,
  } = useFilterLists()

  const { selectedFilters, clearFilters } = useCouponFiltersContext()
  const {
    region_id,
    category,
    offers,
    type,
    status,
    language_id,
    sectoring,
    scopes,
    is_exclusive,
    is_personal
  } = selectedFilters.getFormData();

  const categoryUniqueValues = useMemo(() => {
    const ignoreCategoryIds: number[] = [];
    category.forEach(id => {
      const categoryIds = categoryList[id]?.category_ids;
      if (categoryIds?.length > 1) {
        ignoreCategoryIds.push(...categoryIds.slice(1))
      }
    })

    return category.filter(id => !ignoreCategoryIds.includes(id))
  }, [category, categoryList])

  const handleDeleteGroup = useCallback<HandleDeleteGroupFunction>((field, value) => () => {
    field.setValue(value);
    selectedFilters.handleSubmit();
  }, [selectedFilters])

  const handleDeleteItem = useCallback<HandleDeleteItemFunction>(field => values => {
    field.removeFromArray(values);
    selectedFilters.handleSubmit();
  }, [selectedFilters])

  const showFilters = region_id?.length
    || categoryUniqueValues?.length
    || offers?.length
    || type?.length
    || status?.length
    || language_id?.length
    || sectoring?.length
    || scopes?.length
    || is_exclusive
    || is_personal

  return showFilters ? (
    <Paper classes={{ root: classes.container }}>
      <div className={classes.itemList}>

        <EntityChips
          withGrouping
          groupedLabel={t('filters.region_id')}
          itemsById={regionList}
          value={isEmpty(regionList) ? null : region_id}
          onMultiDelete={handleDeleteItem<number[], typeof selectedFilters.fields.region_id>(selectedFilters.fields.region_id)}
          onGroupDelete={handleDeleteGroup(selectedFilters.fields.region_id, [])}
          variant="filled"
        />

        <EntityChips
          withGrouping
          groupedLabel={t('filters.category_id')}
          itemsById={categoryList}
          value={isEmpty(categoryList) ? null : categoryUniqueValues as unknown as number[]}
          onGroupDelete={handleDeleteGroup(selectedFilters.fields.category, [])}
          variant="filled"
        />

        <EntityChips
          withGrouping
          groupedLabel={t('filters.offers')}
          itemsById={offerList}
          value={isEmpty(offerList) ? null : offers}
          onMultiDelete={handleDeleteItem<number[], typeof selectedFilters.fields.offers>(selectedFilters.fields.offers)}
          onGroupDelete={handleDeleteGroup(selectedFilters.fields.offers, [])}
          variant="filled"
        />

        <EntityChips
          withGrouping
          groupedLabel={t('filters.couponType')}
          itemsById={promoActionTypeList}
          value={isEmpty(promoActionTypeList) ? null : type as unknown as number[]}
          onMultiDelete={handleDeleteItem<number[], typeof selectedFilters.fields.type>(selectedFilters.fields.type)}
          onGroupDelete={handleDeleteGroup(selectedFilters.fields.type, [])}
          variant="filled"
        />

        <EntityChips
          withGrouping
          groupedLabel={t('filters.couponStatus')}
          itemsById={promoCouponStatusList}
          value={isEmpty(promoCouponStatusList) ? null : status as unknown as number[]}
          onMultiDelete={handleDeleteItem<number[], typeof selectedFilters.fields.status>(selectedFilters.fields.status)}
          onGroupDelete={handleDeleteGroup(selectedFilters.fields.status, [])}
          variant="filled"
        />

        <EntityChips
          withGrouping
          groupedLabel={t('filters.couponLanguage')}
          itemsById={languageList}
          value={isEmpty(languageList) ? null : language_id}
          onMultiDelete={handleDeleteItem<number[], typeof selectedFilters.fields.language_id>(selectedFilters.fields.language_id)}
          onGroupDelete={handleDeleteGroup(selectedFilters.fields.language_id, [])}
          variant="filled"
        />

        <EntityChips
          withGrouping
          groupedLabel={t('filters.couponSectoring')}
          itemsById={couponSectorList}
          value={isEmpty(couponSectorList) ? null : sectoring}
          onMultiDelete={handleDeleteItem<number[], typeof selectedFilters.fields.sectoring>(selectedFilters.fields.sectoring)}
          onGroupDelete={handleDeleteGroup(selectedFilters.fields.sectoring, [])}
          variant="filled"
        />

        <EntityChips
          withGrouping
          groupedLabel={t('filters.couponScopes')}
          itemsById={couponScopeList}
          value={isEmpty(couponScopeList) ? null : scopes}
          onMultiDelete={handleDeleteItem<number[], typeof selectedFilters.fields.scopes>(selectedFilters.fields.scopes)}
          onGroupDelete={handleDeleteGroup(selectedFilters.fields.scopes, [])}
          variant="filled"
        />

        <EntityChips
          itemsById={{ 1: { name: t('filters.isExclusive') } }}
          value={is_exclusive ? [1] : undefined}
          onDelete={() => handleDeleteGroup(selectedFilters.fields.is_exclusive, false)}
          variant="filled"
        />

        <EntityChips
          itemsById={{ 1: { name: t('filters.isPersonal') } }}
          value={is_personal ? [1] : undefined}
          onDelete={() => handleDeleteGroup(selectedFilters.fields.is_personal, false)}
          variant="filled"
        />

      </div>
      <IconButton
        className={classes.resetButton}
        aria-label={t('resetFilters')}
        onClick={clearFilters}
        size="large"
      >
        <CancelIcon />
      </IconButton>
    </Paper>
  ) : null
}
